import { FormCategory, FormType, TicketingFormCategory } from "@simplyk/common";

import { Translate } from "@/common/hooks/useTranslate";
import {
  Auction as AuctionIcon,
  Card as MembershipIcon,
  Heart as DonationIcon,
  PeerToPeerCampaign as PeerToPeerCampaignIcon,
  Raffle as RaffleIcon,
  Shop as ShopIcon,
  Tickets as EventIcon,
} from "@/icons/outlined";
import { IconElement } from "@/icons/types";

export type CampaignCategory =
  | "event"
  | "auction"
  | "donation"
  | "membership"
  | "shop"
  | "lottery"
  | "sweepstakes"
  | "peerToPeerCampaign"
  | "custom";

type CampaignCategoryDisplay = {
  icon: IconElement;
  getLabel: (t: Translate) => string;
  getHoverLabel: (t: Translate) => string;
};

const CAMPAIGN_CATEGORY_DISPLAY: Record<CampaignCategory, CampaignCategoryDisplay> = {
  event: {
    icon: <EventIcon />,
    getLabel: (t) => t("organization", "campaignCard.category.event"),
    getHoverLabel: (t) => t("organization", "campaignCard.button.event.upcoming"),
  },
  auction: {
    icon: <AuctionIcon />,
    getLabel: (t) => t("organization", "campaignCard.category.auction"),
    getHoverLabel: (t) => t("organization", "campaignCard.button.auction"),
  },
  donation: {
    icon: <DonationIcon />,
    getLabel: (t) => t("organization", "campaignCard.category.donation"),
    getHoverLabel: (t) => t("organization", "campaignCard.button.donation"),
  },
  membership: {
    icon: <MembershipIcon />,
    getLabel: (t) => t("organization", "campaignCard.category.membership"),
    getHoverLabel: (t) => t("organization", "campaignCard.button.membership"),
  },
  shop: {
    icon: <ShopIcon />,
    getLabel: (t) => t("organization", "campaignCard.category.shop"),
    getHoverLabel: (t) => t("organization", "campaignCard.button.shop"),
  },
  lottery: {
    icon: <RaffleIcon />,
    getLabel: (t) => t("organization", "campaignCard.category.lottery"),
    getHoverLabel: (t) => t("organization", "campaignCard.button.lottery"),
  },
  sweepstakes: {
    icon: <RaffleIcon />,
    getLabel: (t) => t("organization", "campaignCard.category.sweepstakes"),
    getHoverLabel: (t) => t("organization", "campaignCard.button.sweepstakes"),
  },
  peerToPeerCampaign: {
    icon: <PeerToPeerCampaignIcon />,
    getLabel: (t) => t("organization", "campaignCard.category.donation"),
    getHoverLabel: (t) => t("organization", "campaignCard.button.donation"),
  },
  custom: {
    icon: <ShopIcon />,
    getLabel: (t) => t("organization", "campaignCard.category.custom"),
    getHoverLabel: (t) => t("organization", "campaignCard.button.custom"),
  },
};

export const getCampaignCategoryDisplay = (category: CampaignCategory): CampaignCategoryDisplay =>
  CAMPAIGN_CATEGORY_DISPLAY[category];

export const getEndedCampaignHoverLabel = (category: CampaignCategory, t: Translate): string => {
  switch (category) {
    case "auction":
      return t("organization", "campaignCard.button.auction");
    case "lottery":
      return t("organization", "campaignCard.button.past.raffle");
    case "sweepstakes":
      return t("organization", "campaignCard.button.sweepstakes");
    default:
      return t("organization", "campaignCard.button.past.event");
  }
};

const TICKETING_CATEGORY: Partial<Record<TicketingFormCategory, CampaignCategory>> = {
  [TicketingFormCategory.Auction]: "auction",
  [TicketingFormCategory.Lottery]: "lottery",
  [TicketingFormCategory.Sweepstakes]: "sweepstakes",
  [TicketingFormCategory.Custom]: "custom",
};

export const getEventCampaignCategory = (formCategory: TicketingFormCategory): CampaignCategory =>
  TICKETING_CATEGORY[formCategory] ?? "event";

const SUPPORT_FORM_CATEGORY: Partial<Record<FormCategory, CampaignCategory>> = {
  [FormCategory.MembershipV2]: "membership",
  [FormCategory.Shop]: "shop",
  [FormCategory.Lottery]: "lottery",
  [FormCategory.Sweepstakes]: "sweepstakes",
  [FormCategory.Custom]: "custom",
};

export const getSupportFormCampaignCategory = ({
  formType,
  formCategory,
}: {
  formType: FormType;
  formCategory: FormCategory | null;
}): CampaignCategory => {
  if (formType === FormType.DonationForm) {
    return formCategory === FormCategory.PeerToPeerCampaign ? "peerToPeerCampaign" : "donation";
  }
  return formCategory ? (SUPPORT_FORM_CATEGORY[formCategory] ?? "event") : "event";
};
